import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { observabilityAppService } from './ObservabilityAppService';
import { PLAN_ID_TO_KEY } from '../components';
import PlanIds from '../constants/planIds';

export const useComparisonPlansV21 = ({
  planBlocks,
  selectedPlanId,
  onPlanSelect,
  disableAllButtons = false,
  hideAllButtons = false,
  isUpdatingCurrency = false,
  showDecimals = false,
}) => {
  const { t } = useTranslation();

  const plans = useMemo(() => {
    if (!planBlocks?.length) {
      return [];
    }

    return planBlocks
      .map(({ planBlockInfo }) => {
        const isSelected = planBlockInfo.planId === selectedPlanId;
        const isCurrent = !!planBlockInfo.isCurrentPlan;

        const createButtonLabel = () => {
          let btnText = isSelected ? 'button_selected_plan' : 'button_choose_plan';

          if (isCurrent) {
            btnText = 'current_plan';
          }

          return t(`comparison-table.plan_blocks.${btnText}`);
        };

        const planNameId = PLAN_ID_TO_KEY[planBlockInfo.planId];

        if (!planNameId) {
          // No data is expected with Free Plan
          if (planBlockInfo.planId !== PlanIds.GB_FREE_PLAN && planBlockInfo.planId !== PlanIds.UNKNOWN_FREE_PLAN) {
            observabilityAppService.notify(
              new Error(`No comparison table data for the plan. ID: ${planBlockInfo.planId}`),
              {
                planBlocks: planBlocks.map(({ planBlockInfo: { isCurrentPlan, planId, planName, planPrice } }) => ({
                  isCurrentPlan,
                  planId,
                  planName,
                  planPrice,
                })),
              }
            );
          }

          return null;
        }

        return {
          planId: planBlockInfo.planId,
          planNameId,
          planNameLabel: planBlockInfo.planName,
          isSelected,
          isRecommended: planBlockInfo.recommended,
          isCurrent,
          price: {
            paymentPeriodLabel: t('comparison-table.per-month'),
            planCurrency: planBlockInfo.planCurrency,
            planFullPrice: planBlockInfo.planPrice,
            planPriceDiscounted: null,
            isUpdatingCurrency,
            showDecimals,
          },
          buttonLabel: createButtonLabel(),
          buttonClickHandler: () => {
            if (isSelected || typeof onPlanSelect !== 'function') return;

            onPlanSelect(planBlockInfo.planId);
          },
          buttonDisabled: disableAllButtons || isCurrent || typeof onPlanSelect !== 'function',
          buttonHidden: hideAllButtons,
        };
      })
      .filter(Boolean)
      .filter((plan, _index, allPlans) => {
        const normalizeLabel = (label: string) => label.trim().toLowerCase();

        const plansInSameTier = allPlans.filter((candidate) => candidate.planNameId === plan.planNameId);
        const plansWithSameLabel = allPlans.filter(
          (candidate) => normalizeLabel(candidate.planNameLabel) === normalizeLabel(plan.planNameLabel)
        );
        const duplicatePlans = plansInSameTier.length > 1 ? plansInSameTier : plansWithSameLabel;

        if (duplicatePlans.length <= 1) {
          return true;
        }

        // Current + recommended can share a tier key or display label (e.g. two Standard plan ids).
        // Prefer the recommended column so the upgrade target stays visible without duplicating the label.
        if (plan.isRecommended) {
          return true;
        }

        if (duplicatePlans.some((candidate) => candidate.isRecommended)) {
          return false;
        }

        return plan.isCurrent && !duplicatePlans.some((candidate) => candidate.isCurrent);
      });
  }, [
    t,
    planBlocks,
    hideAllButtons,
    selectedPlanId,
    onPlanSelect,
    disableAllButtons,
    isUpdatingCurrency,
    showDecimals,
  ]);

  const scrollIntoViewButtonLabel = useMemo(() => {
    switch (plans.length) {
      case 1:
        return t('upgrade-popup.see-the-details');

      case 2:
        return t('upgrade-popup.compare-two-plans', {
          fromPlanName: plans[0]?.planNameLabel,
          toPlanName: plans[1]?.planNameLabel,
        });

      default:
        return t('upgrade-popup.button-text.compare-plans');
    }
  }, [t, plans]);

  return { scrollIntoViewButtonLabel, plans };
};
